import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, ListBullets, DownloadSimple, Funnel } from "@phosphor-icons/react";
import { toast } from "sonner";
import MonthlyChart from "@/components/MonthlyChart";

const ING = ["taquilla", "cantina", "rifas", "merchandising", "publicidad", "donaciones", "otros"];
const GAS = ["arbitros", "seguridad", "ambulancia", "limpieza", "personal", "comida", "bebida", "material", "otros"];

const fmtEUR = (n) => (Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

export default function CajaPartido() {
  const [summary, setSummary] = useState(null);
  const [matches, setMatches] = useState([]);
  const [activeMatch, setActiveMatch] = useState(null);
  const [selected, setSelected] = useState("active"); // 'active' | match_id

  useEffect(() => {
    (async () => {
      try {
        const [ms, active] = await Promise.all([
          api.get("/matches"),
          api.get("/matches/active"),
        ]);
        setMatches(ms.data);
        setActiveMatch(active.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const currentMatchId = useMemo(() => {
    if (selected === "active") return activeMatch?.id || null;
    return selected;
  }, [selected, activeMatch]);

  const currentMatch = useMemo(() => {
    if (selected === "active") return activeMatch;
    return matches.find((m) => m.id === selected) || null;
  }, [selected, activeMatch, matches]);

  const load = async () => {
    if (!currentMatchId) { setSummary({ ingresos: 0, gastos: 0, beneficio: 0, ingresos_by_cat: {}, gastos_by_cat: {} }); return; }
    try {
      const { data } = await api.get("/transactions/summary", { params: { match_id: currentMatchId } });
      setSummary(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [currentMatchId]);

  const exportCSV = async () => {
    if (!currentMatchId) { toast.error("Selecciona un partido"); return; }
    try {
      const { data, headers } = await api.get("/transactions/export.csv", {
        params: { match_id: currentMatchId },
        responseType: "blob"
      });
      const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : "caja.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("Error al exportar");
    }
  };

  const ingresos = summary?.ingresos || 0;
  const gastos = summary?.gastos || 0;
  const beneficio = summary?.beneficio || 0;
  const positive = beneficio >= 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Módulo 7-8</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Caja del Partido</h1>
          <div className="text-sm text-slate-500 mt-1">
            {currentMatch
              ? <>vs <span className="font-bold text-slate-700">{currentMatch.rival}</span> · {currentMatch.competicion} · {currentMatch.fecha}</>
              : "Sin partido activo — selecciona uno"}
          </div>
        </div>
        <button
          data-testid="caja-export-csv"
          onClick={exportCSV}
          className="px-4 h-11 border border-slate-300 hover:border-slate-900 hover:bg-slate-50 text-slate-800 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98]"
          style={{ borderRadius: 6 }}
        >
          <DownloadSimple size={16} weight="bold" /> Exportar CSV
        </button>
      </div>

      {/* Match selector */}
      <div className="bg-white border border-slate-200 p-4 flex flex-wrap items-end gap-3" style={{ borderRadius: 8 }}>
        <div className="flex items-center gap-2 text-slate-700 text-xs font-bold uppercase tracking-widest mr-2">
          <Funnel size={16} weight="bold" /> Partido
        </div>
        <select
          data-testid="caja-select-match"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="bg-white border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none text-sm min-w-[280px]"
          style={{ borderRadius: 6 }}
        >
          <option value="active">Partido activo</option>
          <optgroup label="Elegir partido">
            {matches.map((m) => (
              <option key={m.id} value={m.id}>vs {m.rival} — {m.fecha}{m.estado === "activo" ? " · ACTIVO" : ""}</option>
            ))}
          </optgroup>
        </select>
      </div>

      {/* Resultado */}
      <div
        className="border border-slate-200 p-6 sm:p-8 relative overflow-hidden"
        style={{ borderRadius: 12, background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)" }}
        data-testid="caja-resultado"
      >
        <div
          className="absolute -right-10 -top-10 w-60 h-60 blur-3xl rounded-full opacity-30"
          style={{ background: positive ? "#22C55E" : "#EF4444" }}
        />
        <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">Ingresos totales</div>
            <div className="font-display font-bold text-5xl mt-2 tracking-tighter text-[#22C55E]" data-testid="caja-ingresos">
              {fmtEUR(ingresos)}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">Gastos totales</div>
            <div className="font-display font-bold text-5xl mt-2 tracking-tighter text-[#EF4444]" data-testid="caja-gastos">
              {fmtEUR(gastos)}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">Beneficio / Pérdida</div>
            <div className="font-display font-bold text-5xl mt-2 tracking-tighter" style={{ color: positive ? "#22C55E" : "#EF4444" }} data-testid="caja-beneficio">
              {positive ? "+" : ""}{fmtEUR(beneficio)}
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid lg:grid-cols-2 gap-4">
        <BreakdownCard title="Ingresos" icon={ArrowUp} color="#22C55E" categories={ING} values={summary?.ingresos_by_cat || {}} />
        <BreakdownCard title="Gastos" icon={ArrowDown} color="#EF4444" categories={GAS} values={summary?.gastos_by_cat || {}} />
      </div>

      {/* Evolución mensual (todas las temporadas / filtrable) */}
      <MonthlyChart />

      <div className="flex flex-wrap gap-3">
        <Link to="/ingresos" data-testid="link-ingresos" className="px-5 h-11 inline-flex items-center gap-2 border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/10 font-display font-bold uppercase tracking-wide text-sm" style={{ borderRadius: 6 }}>
          <ListBullets size={16} /> Ver ingresos
        </Link>
        <Link to="/gastos" data-testid="link-gastos" className="px-5 h-11 inline-flex items-center gap-2 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 font-display font-bold uppercase tracking-wide text-sm" style={{ borderRadius: 6 }}>
          <ListBullets size={16} /> Ver gastos
        </Link>
      </div>
    </div>
  );
}

function BreakdownCard({ title, icon: Icon, color, categories, values }) {
  const total = Object.values(values).reduce((s, v) => s + (v || 0), 0);
  return (
    <div className="bg-white border border-slate-200 p-5" style={{ borderRadius: 6 }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon size={20} weight="bold" style={{ color }} />
          <h3 className="font-display font-bold text-2xl uppercase">{title}</h3>
        </div>
        <div className="font-display font-bold text-2xl tracking-tighter" style={{ color }}>
          {fmtEUR(total)}
        </div>
      </div>
      <div className="space-y-2.5">
        {categories.map((c) => (
          <div key={c} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-b-0">
            <span className="capitalize text-slate-700">{c}</span>
            <span className="font-medium tabular-nums">{fmtEUR(values[c] || 0)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
